import { Object as FabricObject } from 'fabric/fabric-impl';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import history from 'modules/overlay-canvas/addons/history';
import UDText from 'ud-ui/components/text';
import {
  ALL_KIT_TYPES,
  AUTO_KIT_TYPE,
  KIT_ELEMENT_LABELS,
  KIT_TYPE_LABELS,
  KitElement,
  KitTypeSetting,
  resolveContrastColor,
  resolveTeamColor,
  TeamColorFill,
} from 'shared/kit';
import { applyFillSource, DEFAULT_TEAM_COLOR_FILL, readTeamColorFill } from 'modules/template-editor/domain/utils/apply-fill-source';
import { FAKE_TEAM_KITS } from 'modules/template-editor/domain/utils/replace-fake-data';
import { selectFabricManager } from 'modules/template-editor/store/editor/selectors';
import Checkbox from 'modules/template-editor/ui/components/elements/checkbox';
import Selector from 'modules/template-editor/ui/components/elements/selector';
import { Item, SubItem } from '../styles';

interface IProps {
  object: FabricObject;
  /** Подпись секции: у фигуры это «Заливка / фон», у текста — «Цвет шрифта». */
  title: string;
  /** Текст умеет краситься контрастно к цвету команды — фигуре такой режим не нужен. */
  allowContrast?: boolean;
}

/**
 * Секция «Заливка»: свой цвет (в `children` — обычный ColorPicker) или цвет формы команды.
 * Выбор пишется в `object.data.fill`; на канве объект сразу перекрашивается фейковыми
 * комплектами, чтобы результат был виден в редакторе, а не только на трансляции.
 */
function TeamColorSource(props: PropsWithChildren<IProps>) {
  const { object, title, allowContrast, children } = props;
  const manager = useSelector(selectFabricManager);
  const [fill, setFill] = useState<TeamColorFill | undefined>(() => readTeamColorFill(object.data));

  useEffect(() => {
    setFill(readTeamColorFill(object.data));
  }, [object]);

  const update = useCallback(
    (next?: TeamColorFill) => {
      object.data = applyFillSource(object.data, next);
      setFill(next);

      if (next) {
        const kits = next.teamIndex === 1 ? FAKE_TEAM_KITS.second : FAKE_TEAM_KITS.first;
        const color = resolveTeamColor(kits, {
          teamIndex: next.teamIndex,
          kitType: next.kitType,
          element: next.element,
          colorIndex: next.colorIndex,
        });
        object.set('fill', next.mode === 'contrast' ? resolveContrastColor(color) : color);
      }

      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const onFocusField = useCallback(() => {
    manager?.render();
    history.saveState();
  }, [manager]);

  return (
    <Item>
      <span>{title}</span>
      <Selector
        onFocus={onFocusField}
        onChange={(event) => {
          update(event?.value ? (fill ?? DEFAULT_TEAM_COLOR_FILL) : undefined);
        }}
        options={sourceOptions}
        value={sourceOptions.find((option) => option.value === Boolean(fill))}
      />
      {!fill && children}
      {fill && (
        <>
          <UDText type={'caption-1'}>Команда</UDText>
          <Selector
            onFocus={onFocusField}
            onChange={(event) => {
              update({ ...fill, teamIndex: event?.value === 1 ? 1 : 0 });
            }}
            options={teamOptions}
            value={teamOptions.find((option) => option.value === fill.teamIndex)}
          />
          <UDText type={'caption-1'}>Комплект</UDText>
          <Selector
            onFocus={onFocusField}
            onChange={(event) => {
              update({ ...fill, kitType: event?.value as KitTypeSetting });
            }}
            options={kitTypeOptions}
            value={kitTypeOptions.find((option) => option.value === (fill.kitType ?? AUTO_KIT_TYPE))}
          />
          <UDText type={'caption-1'}>Элемент формы</UDText>
          <Selector
            onFocus={onFocusField}
            onChange={(event) => {
              update({ ...fill, element: event?.value as KitElement });
            }}
            options={elementOptions}
            value={elementOptions.find((option) => option.value === (fill.element ?? KitElement.SHIRT))}
          />
          <UDText type={'caption-1'}>Цвет</UDText>
          <Selector
            onFocus={onFocusField}
            onChange={(event) => {
              update({ ...fill, colorIndex: event?.value === 1 ? 1 : 0 });
            }}
            options={colorOptions}
            value={colorOptions.find((option) => option.value === (fill.colorIndex ?? 0))}
          />
          {allowContrast && (
            <SubItem style={{ justifyContent: 'space-between' }}>
              <span>Контрастный к цвету команды</span>
              <Checkbox
                checked={fill.mode === 'contrast'}
                onChange={() => {
                  update({ ...fill, mode: fill.mode === 'contrast' ? 'fill' : 'contrast' });
                }}
              />
            </SubItem>
          )}
        </>
      )}
    </Item>
  );
}

export default TeamColorSource;

const sourceOptions = [
  { label: 'Свой цвет', value: false },
  { label: 'Цвет команды', value: true },
];

const teamOptions = [
  { label: 'Команда №1', value: 0 },
  { label: 'Команда №2', value: 1 },
];

const kitTypeOptions = [
  { label: 'Авто (по стороне)', value: AUTO_KIT_TYPE as KitTypeSetting },
  ...ALL_KIT_TYPES.map((type) => ({ label: KIT_TYPE_LABELS[type], value: type as KitTypeSetting })),
];

const elementOptions = Object.values(KitElement).map((element) => ({ label: KIT_ELEMENT_LABELS[element], value: element }));

const colorOptions = [
  { label: 'Основной', value: 0 },
  { label: 'Цвет узора', value: 1 },
];
